'use client';

import React from 'react';
import { createRoot } from 'react-dom/client';
import Alert from '@mui/material/Alert';
import Portal from '@mui/material/Portal';
import Snackbar from '@mui/material/Snackbar';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const COMPONENT_ID = 'alert-snackbar';

type Props = {
  msg: string;
  severity?: 'error' | 'success' | 'info' | 'warning';
  autoHideDuration?: number | null;
};

let currentAlertRoot: ReturnType<typeof createRoot> | null = null;
let currentTimeout: NodeJS.Timeout | null = null;

const setAlertValue = ({
  severity = 'error',
  msg,
  autoHideDuration = severity === 'error' ? 4000 : 2000,
}: Props) => {
  clearAlert();
  if (!msg) {
    return;
  }
  const domNode = document.createElement('div');
  const root = createRoot(domNode);
  currentAlertRoot = root;
  root.render(
    <Portal>
      <Snackbar
        className={COMPONENT_ID}
        open={true}
        autoHideDuration={autoHideDuration}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Alert
          severity={severity}
          variant="standard"
          iconMapping={{
            error: <ErrorIcon className="!text-orange-600" />,
            success: <CheckCircleIcon className="!text-green-500" />,
          }}
          closeText="关闭"
        >
          {msg}
        </Alert>
      </Snackbar>
    </Portal>,
  );

  if (autoHideDuration) {
    currentTimeout = setTimeout(() => {
      clearAlert();
    }, autoHideDuration);
  }
};

const clearAlert = () => {
  if (currentTimeout) {
    clearTimeout(currentTimeout);
    currentTimeout = null;
  }

  if (currentAlertRoot) {
    currentAlertRoot.unmount();
    currentAlertRoot = null;
  }
};

export default Object.assign(setAlertValue, { clearAlert });
